/**
 * <roro-info-drawer> — panel de «Характеристики / Догляд / Доставка» de la ficha
 * (`blocks/roro-pdp-accordions.liquid`).
 *
 * Cada fila abre el mismo panel con su texto: en escritorio entra por la
 * derecha, en móvil sube desde abajo. El texto ya viene pintado en Liquid (un
 * bloque oculto por fila); aquí solo se decide cuál se enseña, se abre y se
 * cierra el panel y se cuida el foco.
 *
 * Mismo comportamiento que «Shop the look» (`assets/roro-shop-the-look.js`) y
 * el quick add: bloqueo de scroll con `overflow: hidden` en el body, clase
 * `roro-sheet-open` (esconde la barra pinchada de la ficha), reflow forzado en
 * vez de rAF para arrancar la transición, Escape, trampa de foco y deslizar
 * hacia abajo para cerrar en táctil. Sin `pushState`: es el origen del salto
 * arriba en iPhone que tuvo el carrito.
 *
 * El panel se saca al final del <body> al conectar: la columna de detalles es
 * pinchada en escritorio y un `position: fixed` dentro de un ancestro con
 * transform o contain se recortaría. Al desconectar (el editor re-renderiza el
 * bloque) se quita con él.
 */
(function () {
  'use strict';

  if (customElements.get('roro-info-drawer')) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var CLOSE_MS = 300;
  var DRAG_CLOSE_PX = 90;

  class RoroInfoDrawer extends HTMLElement {
    connectedCallback() {
      this.sheet = this.sheet || this.querySelector('[data-info-sheet]');
      if (!this.sheet) return;

      this.panel = this.sheet.querySelector('[data-info-panel]');
      this.titleEl = this.sheet.querySelector('[data-info-title]');
      this.scroller = this.sheet.querySelector('[data-info-scroll]');
      this.closeBtn = this.sheet.querySelector('.roro-info-sheet__close');
      this.trigger = null;
      this.closeTimer = null;

      this.onRowClick = this.onRowClick.bind(this);
      this.onSheetClick = this.onSheetClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      if (this.sheet.parentNode !== document.body) document.body.appendChild(this.sheet);

      this.addEventListener('click', this.onRowClick);
      this.sheet.addEventListener('click', this.onSheetClick);
      this.panel.addEventListener('touchstart', this.onTouchStart, { passive: true });
      // No pasivo: hay que poder cancelar el scroll de la página mientras se arrastra.
      this.panel.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.panel.addEventListener('touchend', this.onTouchEnd);
      this.panel.addEventListener('touchcancel', this.onTouchEnd);
    }

    disconnectedCallback() {
      if (!this.sheet) return;
      this.removeEventListener('click', this.onRowClick);
      document.removeEventListener('keydown', this.onKeydown);
      // Que no se quede la página bloqueada si el editor quita el bloque abierto.
      if (!this.sheet.hidden) {
        document.body.classList.remove('roro-sheet-open');
        this.unlockScroll();
      }
      this.sheet.remove();
      this.sheet = null;
    }

    onRowClick(event) {
      var opener = event.target.closest('[data-info-open]');
      if (!opener || !this.contains(opener)) return;
      event.preventDefault();
      this.open(opener);
    }

    onSheetClick(event) {
      if (event.target.closest('[data-info-close]')) this.close();
    }

    open(opener) {
      var key = opener.getAttribute('data-info-open');
      var pane = this.sheet.querySelector('[data-info-pane="' + key + '"]');
      if (!pane) return;

      window.clearTimeout(this.closeTimer);

      this.sheet.querySelectorAll('[data-info-pane]').forEach(function (el) {
        el.hidden = el !== pane;
      });

      var label = opener.querySelector('.roro-accordion-item__title');
      if (this.titleEl) this.titleEl.textContent = label ? label.textContent.trim() : '';
      if (this.scroller) this.scroller.scrollTop = 0;

      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger = opener;
      opener.setAttribute('aria-expanded', 'true');

      this.sheet.hidden = false;
      document.body.classList.add('roro-sheet-open');
      this.lockScroll();
      /* Reflow forzado, no rAF: con la pestaña sin pintar el rAF no llega y el
         panel se quedaba abierto pero invisible. Ver el quick add. */
      void this.sheet.offsetHeight;
      this.sheet.classList.add('is-open');
      document.addEventListener('keydown', this.onKeydown);

      if (this.closeBtn) this.closeBtn.focus({ preventScroll: true });
    }

    close() {
      if (!this.sheet || this.sheet.hidden) return;

      this.sheet.classList.remove('is-open', 'is-dragging');
      this.panel.style.transform = '';
      document.body.classList.remove('roro-sheet-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockScroll();

      var trigger = this.trigger;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      this.trigger = null;

      var sheet = this.sheet;
      this.closeTimer = window.setTimeout(function () {
        sheet.hidden = true;
        if (trigger && document.contains(trigger)) trigger.focus({ preventScroll: true });
      }, CLOSE_MS);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      // En `<html>` y no en el body: con `overflow-x: clip` en el html (base.css),
      // `hidden` en el body lo convierte en contenedor de scroll y lo `sticky`
      // (la columna derecha de la ficha) salta a su sitio de arriba del todo.
      document.documentElement.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
    }

    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    /* ---------- Arrastrar hacia abajo para cerrar (solo móvil) ----------
       Solo arranca si el texto está arriba del todo: con el texto desplazado,
       bajar el dedo es hacer scroll dentro de él, no cerrar. En escritorio el
       panel entra de lado y no se arrastra. */
    onTouchStart(event) {
      if (event.touches.length !== 1 || window.matchMedia('(min-width: 750px)').matches) {
        this.dragArmed = false;
        return;
      }
      this.dragStartY = event.touches[0].clientY;
      this.dragDelta = 0;
      this.dragArmed = !this.scroller || this.scroller.scrollTop <= 0;
      this.dragging = false;
    }

    onTouchMove(event) {
      if (!this.dragArmed) return;
      var delta = event.touches[0].clientY - this.dragStartY;

      if (!this.dragging) {
        if (delta < 8) {
          // Hacia arriba es scroll normal del texto: se suelta el gesto.
          if (delta < 0) this.dragArmed = false;
          return;
        }
        this.dragging = true;
        this.sheet.classList.add('is-dragging');
      }

      event.preventDefault();
      this.dragDelta = Math.max(0, delta);
      this.panel.style.transform = 'translateY(' + this.dragDelta + 'px)';
    }

    onTouchEnd() {
      if (!this.dragging) {
        this.dragArmed = false;
        return;
      }

      this.dragging = false;
      this.dragArmed = false;
      this.sheet.classList.remove('is-dragging');

      if (this.dragDelta > DRAG_CLOSE_PX) {
        this.close();
      } else {
        // Vuelve a su sitio con la transición normal del panel.
        this.panel.style.transform = '';
      }
    }
  }

  customElements.define('roro-info-drawer', RoroInfoDrawer);
})();
